/**
 * 会话来源：从 super-admin 跳转过来时带的 `?token=`。
 *
 * super-admin 的 `guess.vue` 用 `window.open(adminUrl + '?token=' + token)` 新开标签页，
 * token 是**运营账号**的 JWT（sub=atf，带 id / username / cid / exp）。
 *
 * ⚠ 它**不带 A0**——运营是跨代理的，代理由他在顶栏自己选。
 * （玩家 token 才带 kolUserId 作为 A0。）
 *
 * 拿到后立刻从地址栏抹掉：token 留在 URL 里会进浏览器历史、
 * 也会被复制粘贴分享出去，等于把一个还没过期的登录凭据到处传。
 */
import { peekLandingHash } from "./landing.ts";

const KEY = "aeroplane_admin_token";
/**
 * 票存 localStorage，同一个浏览器的所有标签页共用一份（2026-10-23 起）。
 *
 * 原来存 sessionStorage，「关掉标签页就没了，不会留在机器上」，代价是每开一个标签页
 * （从飞书点链接、中键开新页、关浏览器再开）都要重新登录，运营的感受是「登录老过期」。
 * 现在票会留在这台机器上直到过期或退出：退出在任一标签页做都会清掉、其余标签页跟着回登录页
 * （App 监听 storage 事件）；有人在用时自动续票（session-keeper.ts），一次登录最多续 7 天。
 *
 * 代理选择（A0_KEY）、登录前目标页、欢迎提示仍按标签页存——几个标签页各看各的代理是刻意的。
 */
const LEGACY_TAB_KEYS = [KEY, "aeroplane_admin_own_token"];
const local = () => localStorage;
/**
 * 当前选中的 A0。**存起来是为了刷新、重新登录后还在**——之前每次刷新都回落到「代理列表
 * 第一个」，运营选了 12、刷一下变成 13，而且看不出为什么（leo 2026-08-28 报）。
 *
 * 两层，都按人隔开（leo 2026-10-19：每个人的 A0 不一样，登录前是 1201，登录后不能变）：
 *   - sessionStorage：这个标签页当前选的。几个标签页各看各的代理，刷新互不影响；
 *     记录里带着是谁选的，换了人（退出后别人在同一个标签页登录）不继承上一个人的选择；
 *   - localStorage：这个人最后一次选的。票过期重新登录、关掉标签页再开、Discord 授权往返，
 *     都靠它回到原来的代理。
 *
 * ⚠️ 本来该由 super-admin 在跳转时带过来。实测它的票里只有
 * `sub/id/loginTime/username/cid/ip/exp`，**没有任何租户字段**，跳转 URL 也只带
 * `token`——所以配置台只能自己记。`bootA0()` 已经支持 `?a0=` 参数，
 * 那边什么时候带上，什么时候就自动以它为准。
 */
const A0_KEY = "aeroplane_admin_a0";
const A0_BY_ACCOUNT = "aeroplane_admin_a0:";

export interface Session {
  token: string | null;
  /** 从 token 里读出的操作员名，仅用于顶栏显示；鉴权一律以服务端验签为准 */
  operator: string | null;
  expMs: number | null;
}

function decodeClaims(token: string): Record<string, unknown> | null {
  try {
    const p = token.split(".")[1];
    if (!p) return null;
    const json = atob(p.replace(/-/g, "+").replace(/_/g, "/").padEnd(p.length + ((4 - (p.length % 4)) % 4), "="));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    // 解不开就当没有：这里只用来显示名字，解析失败不该让页面打不开
    return null;
  }
}

/**
 * 启动时取一次会话。
 * 优先用 URL 上的 token（刚跳转过来），否则用上次存下的。
 */
/**
 * 认人用的键：票里的 user_id，Discord 票剥掉 `discord:` 前缀（同一个人用账号密码和 Discord 登录是同一份记忆）；
 * 中台票没有 user_id，退到 cid / id / username。解不开就是空串，A0 只按标签页记。
 */
export function accountKey(token: string | null): string {
  const c = token ? decodeClaims(token) : null;
  const raw = [c?.user_id, c?.cid, c?.id, c?.username].find((v) => (typeof v === "string" && v) || typeof v === "number");
  return raw == null ? "" : String(raw).replace(/^discord:/, "");
}

const tryGet = (store: () => Storage, k: string) => { try { return store().getItem(k); } catch { return null; } };
const trySet = (store: () => Storage, k: string, v: string | null) => {
  try { if (v == null) store().removeItem(k); else store().setItem(k, v); } catch { /* 隐私模式存不了就只是记不住 */ }
};

/**
 * 启动时决定用哪个 A0。优先级从高到低：
 *   1. URL 的 `?a0=`——super-admin 带过来的（目前它还没带，接口先备好）
 *   2. 这个标签页、这个人上次选的——刷新不该丢掉选择
 *   3. 这个人在这台机器上最后选的——重新登录 / 新开标签页回到原来的代理
 *   4. null——交给调用方回落到代理列表第一个
 * 记住的值调用方还会拿代理列表核一遍，权限被收回的不会静默生效。
 */
export function bootA0(): string | null {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("a0");
  if (fromUrl) {
    rememberA0(fromUrl);
    url.searchParams.delete("a0");
    window.history.replaceState(null, "", url.toString());
    return fromUrl;
  }
  const who = accountKey(currentToken());
  const rawTab = tryGet(() => sessionStorage, A0_KEY);
  try {
    const tab = JSON.parse(rawTab ?? "null") as { who?: unknown; a0?: unknown } | null;
    if (tab && typeof tab === "object" && tab.who === who && typeof tab.a0 === "string" && tab.a0) return tab.a0;
  } catch {
    // 老格式是裸字符串（发版前开着的标签页）：认一次，不然发版那一刻所有人都被切回第一个代理
    if (rawTab && /^\w+$/.test(rawTab)) return rawTab;
  }
  return who ? tryGet(() => localStorage, A0_BY_ACCOUNT + who) : null;
}

export const rememberA0 = (a0: string | null) => {
  const who = accountKey(currentToken());
  trySet(() => sessionStorage, A0_KEY, a0 ? JSON.stringify({ who, a0 }) : null);
  if (who) trySet(() => localStorage, A0_BY_ACCOUNT + who, a0);
};

/**
 * Discord 回跳地址（notify-hub 送回 `…/discord/callback?token=xxx`）。
 * 那里的 `?token=` 是**一次性登录凭证**，要由 Gate 拿去 `/admin/api/login/token` 换票，
 * 不是能直接用的管端票。与 lib/landing.ts 的收口规则是同一个路径判断。
 */
export const isDiscordCallback = (pathname: string) => /\/discord\/callback\/?$/.test(pathname);

export function bootSession(): Session {
  const url = new URL(window.location.href);
  /*
   * ⚠ Discord 回跳时**不要**从地址栏取 token。App 挂载时这里先于 Gate 跑：
   * 把那枚一次性凭证当票存起来并抹掉地址栏之后，Gate 看不到 `?token=`
   * 就不会去换票，页面却因为「有票」而不画登录页——顶栏「未登录」、接口全 401
   * （2026-10-13 John 用 Discord 登录实测）。这里让开，Gate 换完会整页重进，
   * 那时地址栏已被 landingUrl 收回根路径、票已经存好，走下面的分支。
   */
  const fromUrl = isDiscordCallback(url.pathname) ? null : url.searchParams.get("token");
  // 发版前开着的标签页票还在 sessionStorage：搬过来一次，不然发版那一刻所有人都被踢回登录页
  if (!currentToken()) {
    for (const k of LEGACY_TAB_KEYS) { const v = tryGet(() => sessionStorage, k); if (v) trySet(local, k, v); }
  }
  for (const k of LEGACY_TAB_KEYS) trySet(() => sessionStorage, k, null);
  if (fromUrl) {
    trySet(local, KEY, fromUrl);
    // 中台换一个人进来：上一个人换的自有票不能留着用
    trySet(local, OWN_KEY, null);
    url.searchParams.delete("token");
    // 中台从原标签页打开新窗口时会复制 sessionStorage，恢复登录前分享的目标。
    // 链接自带的 hash 优先；仅接收 token 的这次回跳可以使用短期记录。
    if (!url.hash) url.hash = peekLandingHash();
    window.history.replaceState(null, "", url.toString());
  }
  const token = fromUrl ?? currentToken();
  if (!token) return { token: null, operator: null, expMs: null };

  const c = decodeClaims(token);
  return {
    token,
    operator: typeof c?.username === "string" ? c.username : null,
    // JWT 的 exp 是秒
    expMs: typeof c?.exp === "number" ? c.exp * 1000 : null,
  };
}

/**
 * 票里的 user_id。Discord 登录签的票是 `discord:<业务 id>`（admin/third-auth.ts 的 loginByOneTimeToken），
 * 前端据此判断「这人就是用 Discord 进来的，肯定绑过」，省一次查询、也不给他再绑一遍。
 */
export const ticketUserId = (token: string | null): string => {
  const v = token ? decodeClaims(token)?.user_id : null;
  return typeof v === "string" ? v : "";
};
export const isDiscordTicket = (token: string | null) => ticketUserId(token).startsWith("discord:");

export const isExpired = (s: Session) => s.expMs != null && s.expMs <= Date.now();

/**
 * 页面启动时拿到的会话，换成存储里现在这一张。
 *
 * 票会被续（api.ts 的续票换了新票、新的过期时间），也会被别的标签页退出清掉；
 * 只看启动时那份 expMs 的话，续了也照样在原来的第 8 小时被门禁拦下。
 * 存储里没有票了（别处退出）按已过期算。
 */
export function liveSession(s: Session): Session {
  if (!s.token) return s;
  const live = currentToken();
  if (!live) return { ...s, expMs: 0 };
  if (live === s.token) return s;
  const c = decodeClaims(live);
  return { token: live, operator: typeof c?.username === "string" ? c.username : s.operator, expMs: typeof c?.exp === "number" ? c.exp * 1000 : null };
}

/** 退出 / 票失效：票是所有标签页共用的，清掉就是这台机器上都退出了（其余标签页靠 storage 事件跟上） */
export const clearSession = () => {
  trySet(local, KEY, null);
  trySet(local, OWN_KEY, null);
  trySet(local, BIND_SKIP_KEY, null);
  for (const k of LEGACY_TAB_KEYS) trySet(() => sessionStorage, k, null);
  // 标签页这份是这个人的选择，退出就清；他自己的那份留在 localStorage，下次登录接着用
  trySet(() => sessionStorage, A0_KEY, null);
};

/** storage 事件里，哪个键是票——App 据此在别的标签页退出 / 登录时跟上 */
export const SESSION_STORAGE_KEY = KEY;

/**
 * 「下次再说」：没绑 Discord 的人这次先跳过绑定页（leo 2026-10-19：有人注册不了 Discord，不能把他关在门外）。
 * 只管到这次登录为止——登录现在跨标签页，所以跟票一起存 localStorage、退出即清，下次登录还会再拦一次。
 * 记着是谁跳过的：同一个标签页换人登录，不继承上一个人的跳过。
 */
const BIND_SKIP_KEY = "aeroplane_admin_bind_skip";
export const skipBindThisSession = () => trySet(local, BIND_SKIP_KEY, accountKey(currentToken()) || "-");
export const bindSkippedThisSession = (): boolean => {
  const v = tryGet(local, BIND_SKIP_KEY);
  return v != null && v === (accountKey(currentToken()) || "-");
};
export const currentToken = () => tryGet(local, KEY);

/**
 * 中台入口 10-31 当天结束关闭（leo 2026-10-21 定）。
 * 后端 /session/exchange 用同一时刻拒绝换票（src/admin/upstream/sunset.ts），两处改要一起改。
 */
export const ZHONGTAI_SUNSET_MS = Date.parse("2026-11-01T00:00:00+09:00");

/**
 * 这次是不是从中台进来的：会话槽里放的不是我们自己的 admin 票。
 * 账号密码、Discord、换票签出来的都带 `type: "admin"`（src/auth/jwt.ts signAdminToken），中台操作员票没有；
 * 自有登录 loginSession() 把自己的票放进同一个槽，所以那种情况是 false。
 * 解不开的票不算中台——那不是中台来的证据，倒计时提示也帮不了他。
 */
export const enteredViaZhongtai = (): boolean => {
  const token = currentToken();
  const c = token ? decodeClaims(token) : null;
  return c != null && c.type !== "admin";
};

/** 离关闭还剩几天：不足一天按一天算，过了是 0 */
export const zhongtaiSunsetDays = (now: number = Date.now()): number =>
  Math.max(0, Math.ceil((ZHONGTAI_SUNSET_MS - now) / 86_400_000));

/**
 * 换来的自有票。
 *
 * 中台 token 用来调中台（/agents 那类透传），自有票用来调我们自己的接口
 * （操作日志等）。两者不能混：中台的密钥我们没有，验不了它的签名。
 */
const OWN_KEY = "aeroplane_admin_own_token";
export const ownToken = () => tryGet(local, OWN_KEY);
export const setOwnToken = (t: string) => trySet(local, OWN_KEY, t);
export const clearOwnToken = () => trySet(local, OWN_KEY, null);

/**
 * JWT 的 exp（毫秒）。解不开按 0（已过期）处理——解不开的票发出去也是 401。
 *
 * 放在这里而不是 api.ts：纯函数才能被单测钉住。api.ts 里那份判断曾经不存在，
 * 过期的自有票被缓存原样返回，整页死在 401 上。
 */
export function tokenExpMs(token: string): number {
  try {
    const p = token.split(".")[1];
    const j = JSON.parse(atob(p.replace(/-/g, "+").replace(/_/g, "/").padEnd(p.length + ((4 - (p.length % 4)) % 4), "=")));
    return typeof (j as { exp?: unknown }).exp === "number" ? (j as { exp: number }).exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/**
 * 一个 401 是不是「这张票不行了」。服务端缺 a0 也回 10068 + 401（src/db/connection.ts、
 * adminA0 的调用方），那是页面没带租户，重新登录解决不了，不能因此把人踢回登录页。
 * 回包不是信封（网关直接回的 401）一律按票失效算。
 */
export const isTicketRejection = (body: { code?: unknown; message?: unknown } | null): boolean =>
  !(typeof body?.message === "string" && body.message.includes("租户标识"));

/**
 * 账号密码登录成功后落盘：这张票就是我们自己签的 admin 票，既当「会话」又当「自有票」——
 * 没有中台上游票可换，所以两个槽位放同一张。之后 ensureOwnToken 看到它没过期就直接用。
 */
export const loginSession = (token: string) => {
  trySet(local, KEY, token);
  trySet(local, OWN_KEY, token);
};

/**
 * 能不能续：账号密码或 Discord 登录的票（会话槽和自有票是同一张、user_id 是 acct: / discord:）。
 * 中台进来的不续——它的自有票过期了由 ensureOwnToken 拿中台票重新换。服务端规则见 src/admin/session-renew.ts。
 */
export function renewableToken(): string | null {
  const t = currentToken();
  if (!t || t !== ownToken()) return null;
  const c = decodeClaims(t);
  const uid = typeof c?.user_id === "string" ? c.user_id : "";
  return c?.type === "admin" && (uid.startsWith("acct:") || uid.startsWith("discord:")) ? t : null;
}

/** 续票成功：两个槽位换成新票。只在票还是续之前那张时换——续的途中别处退出或换了人，不能把旧身份写回去 */
export const replaceRenewedToken = (was: string, next: string) => {
  if (currentToken() !== was) return;
  trySet(local, KEY, next);
  trySet(local, OWN_KEY, next);
};
